import { useMemo } from 'react'
import { Empty, List, Space, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchAuditPage } from '@/features/audit/auditApi'
import { queryKeys } from '@/shared/api/queryKeys'
import { defaultDayRange, rangeToIso } from '@/shared/lib/dayRange'
import { EMPTY_TABLE_STATE } from '@/shared/ui/table/tableState'
import { formatDateTime } from '@/shared/lib/format'
import { objectTypeLabel } from '@/shared/lib/objectType'
import { shortId } from '@/shared/lib/shortId'
import { OperationStatusTag } from '@/shared/ui/StatusTag'

/**
 * Bosh sahifadagi "oxirgi hodisalar" — audit jurnalining so'nggi yozuvlari
 * (oxirgi 7 kun). To'liq jurnal `/audit` da — bu yerda faqat qisqa ko'rinish,
 * operator kirishi bilanoq "hozir nima bo'ldi" savoliga javob beradi.
 *
 * ⚠️ 8 EMAS, 5. Yonidagi ustunda ikkita shunday ro'yxat bor va ikkalasi
 * 8 tadan bo'lganda o'ng ustun chap ustundan ikki barobar uzayib ketardi:
 * sahifaning yarmi bo'sh joy bo'lib qolardi.
 */
const RECENT_LIMIT = 5

export function RecentAuditPanel() {
  const { t } = useTranslation()

  // ⚠️ Oyna TENANT zonasida: `dayjs()` brauzernikini olardi va Kobul
  // tenantida "oxirgi 7 kun" bir kunga surilib ketardi (`dayRange.ts`).
  const range = useMemo(() => rangeToIso(defaultDayRange(7)), [])

  const query = useQuery({
    queryKey: queryKeys.audit.recent,
    queryFn: ({ signal }) =>
      fetchAuditPage(range)({ ...EMPTY_TABLE_STATE, pageSize: RECENT_LIMIT }, signal),
    // Endpoint boshqa agent tomonidan ayni paytda yozilmoqda — hali javob
    // bermasa panel butun sahifani yiqitmasin, shunchaki bo'sh holat chizadi
    retry: false,
  })

  if (query.isError) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('dashboard.recentAuditNotReady')}
      </Typography.Text>
    )
  }

  return (
    <List
      loading={query.isPending}
      dataSource={query.data ? [...query.data.items] : []}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('audit.subscriberEmpty')} /> }}
      renderItem={(entry) => (
        <List.Item key={entry.id}>
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Space align="center" size={8}>
              <OperationStatusTag status={entry.result} />
              <Typography.Text strong>
                {t(`audit.action.${entry.action}`, { defaultValue: entry.action })}
              </Typography.Text>
              {entry.targetId ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {/* ⚠️ Nuqson #5 — bu yerda ham normallashtiriladi.
                      Bosh sahifa va `/audit` bitta jurnalni ko'rsatadi;
                      bittasida `subscriber`, ikkinchisida `Abonent`
                      chiqishi ularni turli manba deb o'ylatardi. */}
                  {/* ⚠️ B12 — qisqartirish `shared/lib/shortId.ts` da.
                      Ilgari bu yerda SHARTSIZ `slice(0, 8) + '…'` turardi:
                      8 belgidan qisqa qiymat («SYSTEM») kesilgandek
                      ko'rinardi, `/audit` da esa o'sha qiymat butun
                      chizilardi. Endi ikkala ekran bir xil. */}
                  {objectTypeLabel(entry.targetType, t) ?? ''} {shortId(entry.targetId)}
                </Typography.Text>
              ) : null}
            </Space>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {formatDateTime(entry.at)} ·{' '}
              {entry.actorId
                ? `${objectTypeLabel(entry.actorType, t) ?? ''} ${shortId(entry.actorId)}`
                : t('audit.systemActor')}
            </Typography.Text>
          </Space>
        </List.Item>
      )}
    />
  )
}
